import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { isPK } from "metabase-lib/v1/types/utils/isa";
import type { DatasetData, RowValue } from "metabase-types/api";

import type { RowCellsWithPkValue } from "../../api/types";
import type { TableRowUpdateHandler } from "../use-table-crud";

import {
  EditDataGridCellState,
  useEditDataGridCellState,
} from "./use-edit-datagrid-cell-state";

type UseTableEditingCellProps = {
  data: DatasetData;
  handleRowUpdate: TableRowUpdateHandler;
};

export type TableEditingCell = {
  rowIndex: number;
  columnId: string;
  value: RowValue;
  actionInput: RowCellsWithPkValue;
};

export function useTableEditingCell({
  data,
  handleRowUpdate,
}: UseTableEditingCellProps) {
  const { getCellState, setCellState, resetCellState } =
    useEditDataGridCellState();
  const [editingCell, setEditingCell] = useState<TableEditingCell | null>(null);

  const handleSelectEditingCell = useCallback(
    (rowIndex: number, columnId: string) => {
      if (!data) {
        return;
      }

      const { rows, cols } = data;
      const row = rows[rowIndex];

      const actionInput = cols.reduce((acc, col, index) => {
        if (isPK(col)) {
          acc[col.name] = rows[rowIndex][index];
        }

        return acc;
      }, {} as RowCellsWithPkValue);

      const columnIndex = cols.findIndex((col) => col.name === columnId);
      const value = row[columnIndex];

      setEditingCell({ rowIndex, columnId, value, actionInput });
    },
    [data],
  );

  const handleCancelEditing = useCallback(() => {
    setEditingCell(null);
  }, []);

  const handleUpdateEditingCell = useCallback(
    async (value: string | null) => {
      if (!editingCell) {
        return;
      }

      handleCancelEditing();

      if (editingCell.value?.toString() !== value?.toString()) {
        setCellState(
          editingCell.columnId,
          editingCell.rowIndex,
          EditDataGridCellState.Updating,
        );

        const result = await handleRowUpdate({
          input: editingCell.actionInput,
          params: { [editingCell.columnId]: value },
          shouldPerformOptimisticUpdate: true,
        });

        if (!result) {
          setCellState(
            editingCell.columnId,
            editingCell.rowIndex,
            EditDataGridCellState.Error,
          );
        } else {
          resetCellState(editingCell.columnId, editingCell.rowIndex);
        }
      }
    },
    [
      editingCell,
      handleCancelEditing,
      handleRowUpdate,
      resetCellState,
      setCellState,
    ],
  );

  return {
    editingCell,
    getCellState,
    handleSelectEditingCell,
    handleUpdateEditingCell,
    handleCancelEditing,
  };
}

/**
 * Restores keyboard focus to the grid container once an inline cell editor
 * closes (Escape, Enter, or a click-away commit). Without this, focus
 * silently drops to <body> the moment the cell's own <input>/<textarea>
 * unmounts, since nothing else on the page claims it — a real WCAG 2.4.3
 * defect (focus not returning after an interaction), confirmed live during
 * this project's accessibility audit.
 *
 * Kept as its own small hook, separate from useTableEditingCell above,
 * because the grid's own scroll-container ref only becomes available from
 * useDataGridInstance, which itself depends on this file's editingCell via
 * the column options it's given — calling this after both are available
 * avoids a circular hook dependency.
 */
export function useRestoreGridFocusOnCellEditEnd(
  editingCell: TableEditingCell | null,
  gridRef: RefObject<HTMLDivElement>,
) {
  const wasEditingRef = useRef(editingCell !== null);

  useEffect(() => {
    if (wasEditingRef.current && editingCell === null) {
      gridRef.current?.focus();
    }
    wasEditingRef.current = editingCell !== null;
  }, [editingCell, gridRef]);
}
